import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setMemberAdded,
  selectMemberAdded,
  setSelectedMenu,
  setSelectedMenuId
} from "../../redux/features/appData/appDataSlice";
import {
  apiGetWorkspaceUsers,
  apiAddWorkspaceUser,
  apiRemoveWorkspaceUser,
  apiAddInvite,
  apiGetWorkspaceInvites,
  apiDeleteInvite,
  apiResendInvite,
  apiGetWorkspace,
  apiDeleteWorkspace,
  apiSetCurrentWorkspace,
} from "../../services/WorkspaceService";
import { 
  apiGetUserByEmail,
  apiGetUser,
} from "../../services/UserService";
import { validateEmail } from "../shared/responseValidators";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { MENUS } from "../config/menus";
import { useNavigate } from "react-router-dom";
import { ModalOnlyUserWarning} from "./ModalOnlyUserWarning";
import { Plus } from "lucide-react"
import config from "environment";

const defaultRoute = config.defaultRoute;


export default function MemberManager() {

  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader(true);
  const navigate = useNavigate();

  const storeUser = useSelector((state) => state.appData.user);
  const memberAdded = useSelector(selectMemberAdded);

  const [addEmail, setAddEmail] = useState(undefined);
  const [validEmail, setValideEmail] = useState(false);
  const [addError, setAddError] = useState("");
  const [workspaceUsers, setWorkspaceUsers] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);

  const [showOnlyUserWarning, setShowOnlyUserWarning] = useState(false);

  useEffect(() => {
    if (!storeUser) {
      return;
    }

    (async () => {
      if (!workspaceUsers) {
        await loadData();
      }
    })();
  }, []);

  useEffect(() => {
    if (memberAdded) {
      (async () => {
        await loadData();
      })();
    }
  }, [memberAdded]);

  const loadData = async () => {
    // get users in the workspace
    const [wsUsers, wsInvites] = await Promise.all([
      apiGetWorkspaceUsers(storeUser.currentWorkspaceId),
      apiGetWorkspaceInvites(storeUser.currentWorkspaceId),
    ]);

    console.log("[memberManager/useEffect] got users: ", wsUsers);
    console.log("[memberManager/useEffect] got invites: ", wsInvites);
    setPendingInvites(wsInvites);
    setWorkspaceUsers(wsUsers);
  };

  const addUserToWorkspace = async () => {
    setAddError("");
    const theEmail = addEmail.toLowerCase();

    const foundUser = await apiGetUserByEmail(theEmail);

    if (!foundUser) {
      const foundInviteIndex = pendingInvites.findIndex(
        (invite) => invite.email === theEmail
      );
      if (foundInviteIndex >= 0) {
        setAddError("You've already invited this email!");
        return;
      }

      const result = await apiAddInvite(
        storeUser.id,
        storeUser.currentWorkspaceId,
        theEmail
      );

      if (!result) {
        setAddError("Something went wrong with the invite!");
        return;
      }

      setPendingInvites([...pendingInvites, result]);

      toast("Invite to workspace sent.", toastSuccess);
      return;
    }

    const existingUserIndex = workspaceUsers.findIndex(
      (user) => user.id === foundUser.id
    );
    if (existingUserIndex >= 0) {
      setAddError("Already added that user.");
      return;
    }

    if (foundUser.id === storeUser.id) {
      setAddError("Cannot add yourself, silly Goose! 🦆");
      return;
    }

    // good to go
    const workspace = await apiAddWorkspaceUser(
      storeUser.currentWorkspaceId,
      foundUser.id
    );

    const wsUsers = await apiGetWorkspaceUsers(storeUser.currentWorkspaceId);

    setWorkspaceUsers(wsUsers);

    dispatch(setMemberAdded(true));
    toast("Added user to workspace.", toastSuccess);
  };

  // is passed the user id
  const handleRemoveUser = async (e) => {
    setAddError("");
    // make sure the user has more than one workspace
    if (e.target.value === storeUser.id && storeUser.workspaces.length === 1) {
      setAddError("This is your only workspace. You must have at least one.");
      return;
    }

    // see if there is more than one user in the workspace
    const theWorkspace = await apiGetWorkspace(storeUser.currentWorkspaceId);

    if (!theWorkspace) {
      setAddError("Unable to find the current workspace.");
      return;
    }

    // this has to be the current user
    if (theWorkspace.users.length === 1) {
      setShowOnlyUserWarning(true);
      return;
    }

    // if not, go ahead and delete it
    await removeUser(e.target.value);
  }

  // do the dirty work
  const removeUser = async (userId) => {
    if (!storeUser.currentWorkspaceId) {
      return;
    }

    const result = await apiRemoveWorkspaceUser(storeUser.currentWorkspaceId, userId);
    if (!result) {
      toast("Unable to remove user..", toastFail);
      return;
    }

    dispatch(setMemberAdded(true));

    // now reload the users
    const wsUsers = await apiGetWorkspaceUsers(storeUser.currentWorkspaceId);
    setWorkspaceUsers([...wsUsers]);

    // if it is the same user, pick the first workspace and reload it all
    if (userId === storeUser.id) {
      const apiUser = await apiGetUser(storeUser.id);

      const newWorkspaceId = storeUser.workspaces[0].id;
      await apiSetCurrentWorkspace(storeUser.id, newWorkspaceId);

      const newUser = { ...apiUser, currentWorkspaceId: newWorkspaceId };
      dispatch(setUser(newUser));

      dispatch(setSelectedMenu(MENUS.HOME));
      dispatch(setSelectedMenuId(undefined));

      // const ct = await apiGetWorkspaceTargets(workspaceId);
      // console.log("[CollectionSidebar] changeWorkspace - reloading: ", workspaceId);
      reloadAll(newWorkspaceId);

      navigate(defaultRoute);
      return;
    }
  };

  const deleteWorkspace = async (workspaceId) => {
    const result = await apiDeleteWorkspace(workspaceId);

    if (!result) {
      toast("Unable to delete workspace..", toastFail);
      return;
    }

    // assume there is at least one more workspace
    const remainingWorkspaces = storeUser.workspaces.filter((workspace) => workspace.id !== workspaceId);
    const newWorkspaceId = remainingWorkspaces[0].id;

    const newUser = { 
      ...storeUser,
      currentWorkspaceId: newWorkspaceId,
      workspaces: remainingWorkspaces
    };

    await apiSetCurrentWorkspace(storeUser.id, newWorkspaceId);
    dispatch(setUser(newUser));

    // reset the menus
    dispatch(setSelectedMenu(MENUS.HOME));
    dispatch(setSelectedMenuId(undefined));

    reloadAll(workspaceId);

    navigate("/");
  }

  const handleEmailChange = (e) => {
    setAddError("");
    const email = e.target.value;

    setAddEmail(email);

    setValideEmail(validateEmail(email));
  };

  const deleteInvite = async (e) => {
    setAddError("");
    const inviteId = e.target.value;
    const result = await apiDeleteInvite(inviteId);
    if (!result) {
      toast("Unable to delete invite..", toastFail);
      return;
    }

    setPendingInvites([
      ...pendingInvites.filter((invite) => invite.id !== inviteId),
    ]);
    toast("Invite removed!", toastSuccess);
  };

  const resendInvite = async (e) => {
    setAddError("");
    const inviteId = e.target.value;
    const result = await apiResendInvite(inviteId);

    if (!result) {
      toast("Unable to resend invite..", toastFail);
      return;
    }

    toast("Invite resent...", toastSuccess);
  };

  return (
    <>
      <div className="workspaces-container">
        <div className="workspaces-header">
          <h2>Add a Member</h2>
          <span style={{ color: "var(--text-color-secondary)" }}>
            Add an existing member by email or invite someone new to effno.
          </span>
        </div>

        <div className="workspaces-adder">
          <input type="text" value={addEmail} onChange={handleEmailChange} />

          <button
            className="button-save"
            onClick={addUserToWorkspace}
            disabled={!validEmail}
          >
            <div className="workspaces-adder-button">
              <Plus size={16} />&nbsp;Add
            </div>
            
          </button>
        </div>
        <div className="workspaces-error">{addError} &nbsp;</div>
      </div>

      {workspaceUsers && workspaceUsers.length !== 0 && (
        <div className="workspaces-members">
          <table>
            <thead>
              <tr>
                <th className="workspaces-members-name">Name</th>
                <th className="workspaces-members-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workspaceUsers.map((user, i) => (
                <tr key={`workspaceMember${i}`}>
                  <td className="workspaces-members-name">
                    <img src={user.avatarURL} />
                    <h2>
                      {user.firstName} {user.lastName}
                    </h2>
                    <span
                      style={{
                        color: "var(--text-color-secondary)",
                        fontWeight: "400",
                      }}>
                      {" "}
                      ({user.email}){" "}
                    </span>
                  </td>
                  <td className="workspaces-members-actions">
                    <button 
                      value={user.id}
                      onClick={handleRemoveUser}
                      className="button-cancel"
                    >
                      {user.id === storeUser.id ? "Leave" : "Remove"}
                    </button>
                    {/* } */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingInvites.length > 0 && (
        <div className="workspaces-invites">
          <h3>Invites</h3>
          <table>
            <thead>
              <tr>
                <th className="workspaces-invites-name">Name</th>
                <th className="workspaces-invites-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingInvites &&
                pendingInvites.map((invite, i) => (
                  <tr key={`pendingInviteRow${i}`}>
                    <td className="workspaces-invites-name">
                      <h2>{invite.email}</h2>
                      <span
                        style={{
                          color: "var(--text-color-secondary)",
                          fontWeight: "400",
                        }}>
                        {" "}
                      </span>
                    </td>
                    <td className="workspaces-invites-actions">
                      <button
                        className="workspaces-invites-action-button"
                        value={invite.id}
                        onClick={deleteInvite}>
                        Delete
                      </button>
                      <button
                        className="workspaces-invites-action-button"
                        value={invite.id}
                        onClick={resendInvite}>
                        Resend
                      </button>
                      {/* } */}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {showOnlyUserWarning && storeUser &&
        <ModalOnlyUserWarning
          isOpen={showOnlyUserWarning}
          onClose={() => setShowOnlyUserWarning(false)}
          onContinue={() => {
            setShowOnlyUserWarning(false)
            deleteWorkspace(storeUser.currentWorkspaceId);
          }}
        />
      }
    </>
  );
}
